/**
 * Smart Genre Image Selector
 * Handles different selection strategies for genre images
 */

/**
 * STATIC GENRE IMAGE MAPPING
 * Pre-mapped genre -> main genre image for instant loading (no API calls needed)
 * Uses {genre-slug}.jpg format when available, falls back to a.jpg
 * UI will handle 404 fallback automatically
 */
export const STATIC_GENRE_IMAGES: Record<string, string> = {
  'Fantasy': '/images/genres/fantasy/fantasy.jpg',
  'Science Fiction': '/images/genres/sci_fi/science-fiction.jpg',
  'Mystery': '/images/genres/mystery/mystery.jpg',
  'Romance': '/images/genres/romance/romance.jpg',
  'Thriller': '/images/genres/thriller/thriller.jpg',
  'Horror': '/images/genres/horror/horror.jpg',
  'Literary Fiction': '/images/genres/literary/litterary-fiction.jpg', // Note: file has typo "litterary"
  'Historical Fiction': '/images/genres/historical/historical-fiction.jpg',
  'Young Adult': '/images/genres/young adult/young-adults.jpg', // File is "young-adults.jpg"
  'Adventure': '/images/genres/adventure/adventure.jpg',
  'Contemporary': '/images/genres/contemporary/contemporary.jpg',
  'Dystopian': '/images/genres/dystopian/dystopian.jpg',
  'Comedy': '/images/genres/comedy/comedy.jpg',
  'Drama': '/images/genres/drama/drama.jpg',
};

/**
 * Get genre main image path
 * Tries {genre-slug}.jpg first, falls back to a.jpg
 */
function getGenreMainImagePath(genre: string): string {
  const genreFolder = mapGenreToFolder(genre);
  const genreSlug = slugify(genre);
  
  // Try genre slug file first (e.g., fantasy.jpg)
  // UI will handle fallback to a.jpg if this 404s
  return `/images/genres/${genreFolder}/${genreSlug}.jpg`;
}

export interface GenreImageConfig {
  strategy: 'random' | 'sequential' | 'specific';
  folder: string;
  specificImage?: string; // For 'specific' strategy
}

/**
 * Get a genre image with flexible selection strategies
 * Default strategy returns the main genre image ({genre-slug}.jpg)
 */
export function getGenreImage(genre: string, config?: Partial<GenreImageConfig>): string {
  // Default: return main genre image (uses STATIC_GENRE_IMAGES)
  if (!config || (!config.strategy && !config.specificImage)) {
    return STATIC_GENRE_IMAGES[genre] || getGenreMainImagePath(genre);
  }

  const defaultConfig: GenreImageConfig = {
    strategy: 'random',
    folder: mapGenreToFolder(genre),
    ...config
  };

  const genreFolder = `/images/genres/${defaultConfig.folder}`;

  switch (defaultConfig.strategy) {
    case 'specific':
      if (defaultConfig.specificImage) {
        return `${genreFolder}/${defaultConfig.specificImage}`;
      }
      // Fallback to main genre image if no specific image provided
      return STATIC_GENRE_IMAGES[genre] || getGenreMainImagePath(genre);

    case 'sequential':
      return getSequentialImageFromFolder(genreFolder);

    case 'random':
    default:
      return getRandomImageFromFolder(genreFolder);
  }
}


/**
 * Get the first image from a genre folder
 * Uses the first image file found in the folder
 */
// Cache for first images to avoid repeated API calls
const firstImageCache = new Map<string, string>();

function getRandomImageFromFolder(folder: string): string {
  // Extract genre name from folder path
  const genre = folder.replace('/images/genres/', '');
  
  // Check cache first
  if (firstImageCache.has(genre)) {
    return firstImageCache.get(genre)!;
  }
  
  // For now, return a placeholder that will be replaced by the async function
  // The actual image will be loaded by getFirstGenreImageAsync
  return `${folder}/loading.jpg`;
}

/**
 * Get the first image from a genre folder dynamically
 */
export async function getFirstGenreImage(genre: string): Promise<string> {
  try {
    // Check cache first
    if (firstImageCache.has(genre)) {
      return firstImageCache.get(genre)!;
    }
    
    const response = await fetch(`/api/genres/${genre}/first-image`)
    if (!response.ok) {
      throw new Error('Failed to get first image')
    }
    const data = await response.json()
    
    // Cache the result
    firstImageCache.set(genre, data.imagePath);
    
    return data.imagePath
  } catch (error) {
    console.error('Error getting first image:', error)
    // Fallback to a default image
    return '/images/backgrounds/victorian-street-scene.jpg'
  }
}

/**
 * Map genre names to folder names
 */
export function mapGenreToFolder(genre: string): string {
  const genreMap: Record<string, string> = {
    'Fantasy': 'fantasy',
    'Science Fiction': 'sci_fi',
    'Mystery': 'mystery',
    'Romance': 'romance',
    'Thriller': 'thriller',
    'Horror': 'horror',
    'Literary Fiction': 'literary',
    'Historical Fiction': 'historical',
    'Young Adult': 'young adult', // Use the actual young adult folder
    'Adventure': 'adventure',
    'Contemporary': 'contemporary', // Use contemporary folder
    'Dystopian': 'dystopian',
    'Comedy': 'comedy',
    'Drama': 'drama',
    // Legacy lowercase support
    'fantasy': 'fantasy',
    'sci-fi': 'sci_fi',
    'mystery': 'mystery',
    'romance': 'romance',
    'thriller': 'thriller',
    'horror': 'horror',
    'literary fiction': 'literary',
    'historical fiction': 'historical',
    'young-adult': 'young adult',
    'adventure': 'adventure',
    'contemporary': 'contemporary',
    'dystopian': 'dystopian',
    'comedy': 'comedy',
    'drama': 'drama'
  };
  
  return genreMap[genre] || genre;
}

/**
 * Get sequential images from a genre folder
 * Looks for numbered files (1.jpg, 2.jpg, etc.)
 */
function getSequentialImageFromFolder(folder: string): string {
  // For now, return the first available image from each genre
  return getRandomImageFromFolder(folder);
}

/**
 * Convert text to URL-safe slug
 * Used for both genres and subgenres
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[&]/g, 'and')        // Replace & with and
    .replace(/[+]/g, '-')           // Replace + with -
    .replace(/[\/]/g, '-')          // Replace / with -
    .replace(/[^a-z0-9-]/g, '-')    // Replace non-alphanumeric with -
    .replace(/-+/g, '-')            // Replace multiple - with single -
    .replace(/^-|-$/g, '');         // Remove leading/trailing -
}

/**
 * Get subgenre image path
 * Returns path to subgenre image file (flat file in genre folder)
 * UI should handle fallback to genre image if this 404s
 */
export function getSubgenreImage(genre: string, subgenre: string): string {
  if (!subgenre || subgenre === 'none' || subgenre === '') {
    return getGenreImage(genre);
  }

  const genreFolder = mapGenreToFolder(genre);
  const subgenreSlug = slugify(subgenre);
  
  // Return flat file path: /images/genres/{genre}/{subgenre-slug}.jpg
  return `/images/genres/${genreFolder}/${subgenreSlug}.jpg`;
}

/**
 * Get all available images for a genre
 */
export function getAvailableGenreImages(genre: string): string[] {
  // This would scan the folder and return all image files
  // Implementation depends on how you want to handle this
  return [`/images/genres/${genre}/image1.jpg`];
}

/**
 * Configuration for different genre image strategies
 */
export const GENRE_IMAGE_STRATEGIES = {
  // Random selection (good for variety)
  RANDOM: { strategy: 'random' as const },
  
  // Sequential (good for consistent ordering)
  SEQUENTIAL: { strategy: 'sequential' as const },
  
  // Specific image (good for exact control)
  SPECIFIC: (imageName: string) => ({ 
    strategy: 'specific' as const, 
    specificImage: imageName 
  }),
} as const;

// Example usage:
// const fantasyImage = getGenreImage('fantasy', GENRE_IMAGE_STRATEGIES.RANDOM);
// const romanceImage = getGenreImage('romance', GENRE_IMAGE_STRATEGIES.SPECIFIC('vintage-cafe.jpg'));
// const thrillerImage = getGenreImage('thriller', GENRE_IMAGE_STRATEGIES.SEQUENTIAL);
